import React, { useContext, useMemo } from 'react'
import {
  CheckCircleIcon,
  CalendarIcon,
  CreditCardIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import { AuthContext } from '../../contexts/AuthContext'
import PageTitle from '../common/PageTitle'
import CurrencyDisplay from '../common/CurrencyDisplay'
import { getDaysRemaining, isExpired, formatTime, formatRemainingTime } from '../../utils/date.util'
import { SUBSCRIPTION_WARNING_THRESHOLDS } from '../../constants/subscriptionPlans'

const ActiveSubscription = ({ onSwitchPlan }) => {
  const { currentUser } = useContext(AuthContext)
  const activeSubscription = currentUser?.activeSubscription

  const subscriptionDetails = useMemo(() => {
    if (!activeSubscription) return null

    const planPrice = parseFloat(activeSubscription.subscription?.price || 0)
    const pricePaid = parseFloat(activeSubscription.pricePaid || 0)
    const monthsPaid = planPrice > 0 ? Math.round(pricePaid / planPrice) : 1
    const expiresAt = activeSubscription.expiresAt
    const subscriptionExpired = isExpired(expiresAt)
    const daysRemaining = getDaysRemaining(expiresAt)

    return {
      planName: activeSubscription.subscription?.name || 'Unknown',
      description: activeSubscription.subscription?.description || '',
      monthsPaid,
      pricePaid,
      expiresAt,
      isExpired: subscriptionExpired,
      daysRemaining,
    }
  }, [activeSubscription])

  const features = useMemo(() => {
    return subscriptionDetails?.description?.split('\n').filter(Boolean) || []
  }, [subscriptionDetails])

  if (!subscriptionDetails) return null

  if (subscriptionDetails.isExpired) {
    return (
      <div className="text-center py-8 min-h-96">
        <PageTitle>Subscription Expired</PageTitle>
        <p className="text-gray-600">
          Please renew your subscription to continue enjoying our services.
        </p>
        <button
          onClick={onSwitchPlan}
          className="mt-8 mx-auto bg-gradient-to-r from-secondary-700 to-secondary-500 text-white px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Renew Subscription
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageTitle>Active Subscription</PageTitle>

      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-xl text-gray-900 capitalize">
                {subscriptionDetails.planName} Plan
              </h2>
              <p className="text-green-600 font-medium">Active Subscription</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-900">
              <CurrencyDisplay amount={subscriptionDetails.pricePaid} />
            </div>
            <p className="text-sm text-gray-600">
              {subscriptionDetails.monthsPaid} month
              {subscriptionDetails.monthsPaid !== 1 ? 's' : ''} paid
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="flex items-center gap-3 p-3 bg-background rounded-lg border border-gray-100">
            <CalendarIcon className="w-5 h-5 text-gray-500" />
            <div>
              <p className="text-sm font-medium text-gray-900">Expires</p>
              <p className="text-sm text-gray-600">{formatTime(subscriptionDetails.expiresAt)}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-background rounded-lg border border-gray-100">
            <CreditCardIcon className="w-5 h-5 text-gray-500" />
            <div>
              <p className="text-sm font-medium text-gray-900">Time Remaining</p>
              <p
                className={`text-sm font-medium ${
                  subscriptionDetails.daysRemaining > SUBSCRIPTION_WARNING_THRESHOLDS.WARNING
                    ? 'text-green-600'
                    : subscriptionDetails.daysRemaining > SUBSCRIPTION_WARNING_THRESHOLDS.CRITICAL
                    ? 'text-orange-600'
                    : 'text-red-600'
                }`}
              >
                {formatRemainingTime(subscriptionDetails.expiresAt)}
              </p>
            </div>
          </div>
        </div>

        {features.length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Plan Features</h3>
            <div className="space-y-2">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-gray-700">{feature.trim()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <div className="flex-1  flex flex-col items-center gap-2">
            <button
              onClick={onSwitchPlan}
              className="w-full bg-background border border-gray-300 text-gray-700 px-4 py-3 rounded-lg font-medium hover:bg-neutral-200-50 transition-colors"
            >
              Switch Plan
            </button>
            <p className="text-xs text-gray-500">
              Switching plans will change your billing cycle and may affect your current benefits.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ActiveSubscription
